import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from './api'

export type LessonPlanningDashboard = {
  academicSession: string
  reviewRequired: boolean
  counts: Record<string, number>
  upcoming: Array<{ _id: string; title: string; date: string; status: string; subjectName: string }>
  missingScheduledPlans: number
}

export type LessonPlanContext = {
  academic: {
    subject: unknown
    plan: unknown
    topic: unknown
    allocations: unknown[]
  }
  timetable: {
    available: boolean
    slots: Array<{ date: string; dayName: string; periodKey: string; label: string }>
    periodsPerWeek?: number
  }
  textbooks: Array<{ editionId?: string; editionTitle?: string; publisherName?: string }>
}

export type LessonPlanTemplate = {
  _id: string
  name: string
  scope: string
  subjectName: string
  capabbleClassKey: string
  periods: number
  methodologyTags: string[]
}

const base = '/acdmc/lesson-planning'

export const fetchLessonPlanningDashboard = () =>
  api.get<{ success: boolean; data: LessonPlanningDashboard }>(`${base}/dashboard`).then((r) => r.data.data)

export const fetchLessonPlanContext = (params: {
  className: string
  section?: string
  subjectName: string
  syllabusTopicId?: string
  from?: string
  to?: string
}) => api.get<{ success: boolean; data: LessonPlanContext }>(`${base}/context`, { params }).then((r) => r.data.data)

export const duplicateLessonPlanApi = (id: string) =>
  api.post(`${base}/plans/${id}/duplicate`).then((r) => r.data.data)

export const transitionLessonPlanApi = (id: string, status: string, comment?: string) =>
  api.post(`${base}/plans/${id}/transition`, { status, comment }).then((r) => r.data.data)

export const fetchLessonTemplates = () =>
  api.get<{ success: boolean; data: LessonPlanTemplate[] }>(`${base}/templates`).then((r) => r.data.data)

export const applyLessonTemplate = (templateId: string) =>
  api.get<{ success: boolean; data: Record<string, unknown> }>(`${base}/templates/${templateId}/apply`).then((r) => r.data.data)

export function useLessonPlanningDashboard() {
  return useQuery({ queryKey: ['acdmc', 'lesson-planning', 'dashboard'], queryFn: fetchLessonPlanningDashboard })
}

export function useLessonPlanContext(enabled: boolean, params: Parameters<typeof fetchLessonPlanContext>[0]) {
  return useQuery({
    queryKey: ['acdmc', 'lesson-planning', 'context', params],
    queryFn: () => fetchLessonPlanContext(params),
    enabled: enabled && Boolean(params.className && params.subjectName),
  })
}

export function useDuplicateLessonPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: duplicateLessonPlanApi,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acdmc'] })
    },
  })
}

export function useLessonTemplates() {
  return useQuery({ queryKey: ['acdmc', 'lesson-planning', 'templates'], queryFn: fetchLessonTemplates })
}
